/**
 * Quotes API - Now uses Supabase
 *
 * This file re-exports from the Supabase quotes API for backwards compatibility.
 */

import { quotesApi as supabaseQuotesApi, quoteStatusLabels, quoteStatusColors } from './supabase/quotes';
import type { QuoteStatus } from './supabase/quotes';

// Re-export status labels and colors
export { quoteStatusLabels, quoteStatusColors };
export type { QuoteStatus };

export const quotesApi = {
  // Get all quotes with optional filters
  async getAll(params?: {
    status?: QuoteStatus;
    leadId?: number | string;
    page?: number;
    pageSize?: number;
  }) {
    const response = await supabaseQuotesApi.getAll({
      leadId: params?.leadId?.toString(),
      status: params?.status,
      page: params?.page,
      pageSize: params?.pageSize,
    });

    // Return in format expected by existing code
    return {
      data: response.data,
      meta: {
        pagination: {
          total: response.count,
          page: params?.page || 1,
          pageSize: params?.pageSize || 25,
        },
      },
    };
  },

  // Get quotes for a specific lead
  async getByLead(leadId: number | string) {
    const data = await supabaseQuotesApi.getByLead(leadId.toString());
    return {
      data,
      meta: {
        pagination: {
          total: data.length,
        },
      },
    };
  },

  // Get a single quote by ID
  async getById(id: number | string) {
    const data = await supabaseQuotesApi.getById(id.toString());
    return { data };
  },

  // Get quote by quote number
  async getByQuoteNumber(quoteNumber: string) {
    return supabaseQuotesApi.getByQuoteNumber(quoteNumber);
  },

  // Create a new quote
  async create(data: any) {
    // console.log('quotesApi.create called with:', { quoteNumber: data.quoteNumber, lead: data.lead });
    try {
      const quoteData = {
        leadId: data.lead?.toString() || null,
        quoteNumber: data.quoteNumber,
        quoteDate: data.quoteDate || new Date().toISOString().split('T')[0],
        validUntil: data.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        clientName: data.clientName || '',
        clientContact: data.clientContact || null,
        clientAddress: data.clientAddress || null,
        clientCity: data.clientCity || null,
        clientState: data.clientState || 'WA',
        clientZip: data.clientZip || null,
        projectName: data.projectName || null,
        projectAddress: data.projectAddress || null,
        projectCity: data.projectCity || null,
        projectState: data.projectState || 'WA',
        projectZip: data.projectZip || null,
        linearFootage: data.linearFootage || 0,
        pricePerFoot: data.pricePerFoot || 25,
        controllerPrice: data.controllerPrice || 0,
        powerSupplyPrice: data.powerSupplyPrice || null,
        includePowerSupply: data.includePowerSupply || false,
        additionalItems: data.additionalItems || [],
        subtotal: data.subtotal || 0,
        taxRate: data.taxRate || 10.3,
        taxAmount: data.taxAmount || 0,
        totalAmount: data.totalAmount || 0,
        depositPercent: data.depositPercent || 50,
        depositAmount: data.depositAmount || 0,
        status: data.status || 'draft',
        userId: null,
      };

      const result = await supabaseQuotesApi.create(quoteData);
      // console.log('Quote created successfully:', result.id);
      return { data: result };
    } catch (error: any) {
      // console.error('Failed to create quote:', error);
      throw new Error(`Quote creation failed: ${error?.message || 'Unknown error'}`);
    }
  },

  // Update an existing quote
  async update(id: number | string, data: any) {
    // Map 'lead' field to 'leadId' for Supabase API compatibility
    const updateData = { ...data };
    if (data.lead !== undefined) {
      updateData.leadId = data.lead?.toString() || null;
      delete updateData.lead;
    }
    const result = await supabaseQuotesApi.update(id.toString(), updateData);
    return { data: result };
  },

  // Delete a quote
  async delete(id: number | string) {
    await supabaseQuotesApi.delete(id.toString());
    return { data: null };
  },

  // Update quote status
  async updateStatus(id: number | string, status: QuoteStatus) {
    return this.update(id, { status });
  },

  // Mark quote as sent
  async markAsSent(id: number | string) {
    return this.updateStatus(id, 'sent');
  },

  // Mark quote as accepted
  async markAsAccepted(id: number | string) {
    return this.updateStatus(id, 'accepted');
  },

  // Mark quote as rejected
  async markAsRejected(id: number | string) {
    return this.updateStatus(id, 'rejected');
  },

  // Generate next quote number
  async generateQuoteNumber(): Promise<string> {
    return supabaseQuotesApi.generateQuoteNumber();
  },
};
